import type React from 'react';

import { Button } from '@change/ds-core-ui';

import { TypedTranslate } from '@change-corgi/core/react/i18n';

import { DynamicTask } from './components/DynamicTask';
import { DynamicTasksLoading } from './components/DynamicTasksLoading';
import { SectionHeader } from './components/SectionHeader';
import { useDynamicTasks } from './hooks/useDynamicTasks';
import { useTaskExpansion } from './hooks/useTaskExpansion';

const LOADING_STATUSES = ['PENDING', 'IN_PROGRESS'];
const HIDE_STATUSES = ['FAILED'];

export const DynamicTasks: React.FC = () => {
	const { data, actions } = useDynamicTasks();
	const {
		tasks,
		stats,
		generationStatus,
		showTaskListToggle,
		toggleTaskListLabel,
		toggleTaskListRemainingCount,
		toggleTaskListIcon,
	} = data;

	const { toggleTaskList } = actions;

	const { isExpanded, toggleExpansion } = useTaskExpansion(0);

	if (LOADING_STATUSES.includes(generationStatus)) {
		return <DynamicTasksLoading />;
	}

	if (HIDE_STATUSES.includes(generationStatus) || stats.count === 0) {
		return null;
	}

	return (
		<div className="bg-background-base flex flex-col gap-5 px-4 py-8 md:p-12" data-qa="dynamic-tasks">
			<SectionHeader completed={stats.completed} total={stats.count} />
			<div className="flex flex-col gap-4">
				<div className="flex flex-col">
					{tasks.map((task) => (
						<DynamicTask
							key={task.index}
							task={task}
							isExpanded={isExpanded(task.index)}
							onToggleExpand={() => toggleExpansion(task.index)}
						/>
					))}
				</div>
				{showTaskListToggle && (
					<Button
						variant="tertiary"
						size="x-small"
						onClick={toggleTaskList}
						data-qa="dynamic-tasks-view-more-button"
						className="bg-fill-transparent text-typography-strong text-size-body-small w-full gap-2"
					>
						<TypedTranslate
							value={toggleTaskListLabel}
							replacements={{ count: String(toggleTaskListRemainingCount) }}
						/>
						{toggleTaskListIcon}
					</Button>
				)}
			</div>
		</div>
	);
};
